import React, { useEffect, useState, useRef, useCallback } from "react"
import type { DiscoverFiltersProps } from "~/react/types/discover"
import { useDiscoverFilters } from "~/react/hooks/useDiscoverFilters"
import { discovers_path, discover_views_path, discover_view_path } from "~/lib/routes"
import { CompanyNameFilter } from "~/react/components/discover_filters/company_name_filter"
import { CompanySizeFilter } from "~/react/components/discover_filters/company_size_filter"
import { IndustryFilter } from "~/react/components/discover_filters/industry_filter"
import { CompanyTypeFilter } from "~/react/components/discover_filters/company_type_filter"
import { KeywordsFilter } from "~/react/components/discover_filters/keywords_filter"
import { FoundedYearFilter } from "~/react/components/discover_filters/founded_year_filter"
import { LocationFilter } from "~/react/components/discover_filters/location_filter"
import { SimilarCompaniesFilter } from "~/react/components/discover_filters/similar_companies_filter"
import { TechnologiesFilter } from "~/react/components/discover_filters/technologies_filter"
import { FundingsFilter } from "~/react/components/discover_filters/fundings_filter"

const t = (key: string) => window.i18n.t(key)

// Helper to ensure value is always an array (handles single string or object from saved searches)
function ensureArray(value: unknown): string[] {
  if (value === undefined || value === null) return []
  if (Array.isArray(value)) {
    return value.map((item) => {
      // Handle [value, label] pairs from location filters
      if (Array.isArray(item)) return String(item[0])
      return String(item)
    })
  }
  if (typeof value === "object") return Object.keys(value)
  return [String(value)]
}

// Cache CSRF token at module level (queried once on first access)
let cachedCsrfToken: string | null = null
function getCsrfToken(): string {
  if (cachedCsrfToken === null) {
    cachedCsrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ""
  }
  return cachedCsrfToken
}

export default function DiscoverFilters({
  currentFilters = {},
  filterOptions,
  savedSearches = [],
  entitlement,
  viewId,
}: DiscoverFiltersProps) {
  const { filters, updateFilter, clearAllFilters, hasFilters } = useDiscoverFilters(currentFilters)
  const [searchName, setSearchName] = useState("")
  const [showAiAssistant, setShowAiAssistant] = useState(!!currentFilters.last_ai_query)
  const [aiQuery, setAiQuery] = useState(currentFilters.last_ai_query || "")
  const aiTextareaRef = useRef<HTMLTextAreaElement>(null)
  // Track if this is the initial mount to avoid re-applying existing filters
  const isInitialMount = useRef(true)
  // Track if we're doing a manual clear to skip auto-apply
  const isManualClear = useRef(false)

  // Auto-resize textarea to fit placeholder on mount
  useEffect(() => {
    if (showAiAssistant && aiTextareaRef.current) {
      const textarea = aiTextareaRef.current
      if (!textarea.value && textarea.placeholder) {
        // Temporarily set placeholder as value to measure height
        const originalValue = textarea.value
        textarea.value = textarea.placeholder
        textarea.style.height = "auto"
        const measuredHeight = textarea.scrollHeight
        textarea.value = originalValue
        textarea.style.height = `${measuredHeight}px`
      }
    }
  }, [showAiAssistant])

  // Get the current view name if editing an existing saved search
  const currentView = viewId ? savedSearches.find((s) => s.id === viewId) : null

  // Initialize search name from current view when component mounts or viewId changes
  useEffect(() => {
    if (currentView) {
      setSearchName(currentView.name)
    }
  }, [viewId, currentView?.name])

  // Show Clear All and Save buttons when there are filters OR when viewing a saved search
  const showActions = hasFilters || !!viewId

  // Defensive fallback - filterOptions should always be provided by prepare_react_props
  // but this prevents runtime errors if something goes wrong during prop passing
  if (!filterOptions) {
    return (
      <div className="p-4 text-center">
        <p className="text-sm text-muted">Loading filters...</p>
      </div>
    )
  }

  // Handle Clear All: clear local state and reload results frame
  const handleClearAll = useCallback(() => {
    // Set flag to skip auto-apply effect (we're manually navigating)
    isManualClear.current = true
    clearAllFilters()
    // Dispatch loading event to show loading overlay
    window.dispatchEvent(new CustomEvent("discover-filters:loading"))
    // Update browser URL to clear query params
    const url = discovers_path()
    window.history.replaceState(null, "", url)
    // Reload results frame with no filters (same pattern as applying filters)
    window.Turbo.visit(url, { frame: "discover_results" })
  }, [clearAllFilters])

  const handleApplyFilters = useCallback(() => {
    // Build query string from filters
    const params = new URLSearchParams()

    Object.entries(filters).forEach(([key, value]) => {
      if (value === undefined || value === null) return

      if (Array.isArray(value)) {
        // Skip empty arrays
        if (value.length === 0) return
        // Handle [value, label] pairs from location filters
        value.forEach((v) => {
          const strValue = Array.isArray(v) ? String(v[0]) : String(v)
          params.append(`${key}[]`, strValue)
        })
      } else if (typeof value === "object") {
        // Handle object values (like {domain: true}) - extract keys
        const keys = Object.keys(value)
        if (keys.length === 0) return
        keys.forEach((k) => params.append(`${key}[]`, k))
      } else {
        params.append(key, String(value))
      }
    })

    // Dispatch loading event to show loading overlay
    window.dispatchEvent(new CustomEvent("discover-filters:loading"))

    // Build the URL and update browser history
    const url = `${discovers_path()}?${params.toString()}`
    window.history.replaceState(null, "", url)

    // Trigger Turbo visit to update results
    window.Turbo.visit(url, { frame: "discover_results" })
  }, [filters])

  // Auto-apply filters when they change (but not on initial mount or manual clear)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    // Skip auto-apply if we just did a manual clear (already navigated)
    if (isManualClear.current) {
      isManualClear.current = false
      return
    }

    const timer = setTimeout(() => {
      handleApplyFilters()
    }, 500) // Debounce 500ms

    return () => clearTimeout(timer)
  }, [handleApplyFilters])

  // Get filters as JSON string for saving
  const getFiltersJson = () => {
    const cleanFilters: Record<string, unknown> = {}
    Object.entries(filters).forEach(([key, value]) => {
      if (value === undefined || value === null) return
      if (Array.isArray(value) && value.length === 0) return
      cleanFilters[key] = value
    })
    return JSON.stringify(cleanFilters)
  }

  return (
    <>
      <div className="collapsable-filters__header">
        <h2 className="collapsable-filters__title">{t("js.back.discover.title")}</h2>

        <button
          type="button"
          className="h-button h-button--secondary h-button--xs h-button--icon"
          aria-label={t("js.back.discover.ai_assistant.title")}
          title={t("js.back.discover.ai_assistant.title")}
          aria-expanded={showAiAssistant}
          onClick={() => setShowAiAssistant(!showAiAssistant)}
        >
          <span className="far fa-sparkles" aria-hidden="true" />
        </button>

        <div className="collapsable-filters__actions">
          {/* Clear All - shows when filters exist */}
          {hasFilters && (
            <button
              type="button"
              className="h-button h-button--link h-button--sm"
              onClick={handleClearAll}
            >
              <strong>{t("js.back.discover.clear_all")}</strong>
            </button>
          )}

          {/* Save Search - shows when filters exist OR when viewing a saved search */}
          {showActions && (
            <div className="dropdown">
              <button
                type="button"
                className="h-button h-button--ghost h-button--sm"
                data-bs-toggle="dropdown"
                disabled={!hasFilters}
              >
                {t("js.back.discover.save")}
              </button>
              <div className="dropdown-menu pull-right">
                <form method="post" action={discover_views_path()}>
                  <input type="hidden" name="authenticity_token" value={getCsrfToken()} />
                  {viewId && (
                    <input type="hidden" name="view[id]" value={viewId} />
                  )}
                  <input type="hidden" name="view[filters]" value={getFiltersJson()} />

                  <div className="m-4">
                    <label className="h-label" htmlFor="save-search-name">
                      {t("js.back.discover.save_search.name_label")}
                    </label>
                    <input
                      id="save-search-name"
                      type="text"
                      name="view[name]"
                      className="h-input h-input--sm h-input--block"
                      value={searchName}
                      onChange={(e) => setSearchName(e.target.value)}
                    />
                  </div>

                  {viewId ? (
                    <>
                      <input
                        type="submit"
                        className="dropdown-menu__submit"
                        value={t("js.back.discover.save_search.create_new")}
                        name="create"
                        formAction={discover_views_path()}
                        formMethod="post"
                        data-loading="none"
                      />
                      <input
                        type="submit"
                        className="dropdown-menu__submit"
                        value={t("js.back.discover.save_search.update")}
                        name="update"
                        formAction={discover_view_path({ id: viewId })}
                        formMethod="put"
                        data-loading="none"
                      />
                    </>
                  ) : (
                    <input
                      type="submit"
                      className="dropdown-menu__submit"
                      value={t("js.back.discover.save_search.save")}
                      name="create"
                    />
                  )}
                </form>
              </div>
            </div>
          )}

          <div className="dropdown">
            <button
              type="button"
              className="h-button h-button--ghost h-button--sm"
              data-bs-toggle="dropdown"
            >
              {t("js.back.discover.load_search")}
            </button>
            {savedSearches.length === 0 ? (
              <div className="dropdown-menu pull-right">
                <div className="empty-space empty-space--sm p-4">
                  <strong className="empty-space__title">{t("js.back.discover.no_saved_searches.title")}</strong>
                  <p className="empty-space__desc">
                    {t("js.back.discover.no_saved_searches.description")}
                  </p>
                </div>
              </div>
            ) : (
              <div className="dropdown-menu main-actions__dropdown pull-right">
                <ul>
                  {savedSearches.map((search) => (
                    <li key={search.id} className="dropdown-menu__item">
                      <a
                        className="dropdown-menu__action"
                        href={`${discovers_path()}?view_id=${search.id}`}
                      >
                        {search.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {showAiAssistant && (
        <form
          className="lookup-ai-assistant"
          action={discovers_path()}
          method="get"
          data-turbo-frame="_top"
          onSubmit={(e) => {
            if (!aiQuery.trim()) {
              e.preventDefault()
            }
          }}
        >
          <label className="ai-assistant__title" htmlFor="ai-assistant-prompt">
            {t("js.back.discover.ai_assistant.title")}
          </label>
          <textarea
            ref={aiTextareaRef}
            id="ai-assistant-prompt"
            name="ai_query[ai_query]"
            className="h-textarea lookup-ai-assistant__input"
            rows={1}
            placeholder={t("js.back.discover.ai_assistant.placeholder")}
            value={aiQuery}
            onChange={(e) => {
              setAiQuery(e.target.value)
              // Auto-grow textarea
              const target = e.target
              target.style.height = "auto"
              target.style.height = `${target.scrollHeight}px`
            }}
          />
          <div className="lookup-ai-assistant__actions">
            <button type="submit" className="h-button h-button--link ml-auto">
              <strong>{t("js.back.discover.apply")}</strong>
            </button>
          </div>
        </form>
      )}

      <div className="collapsable-filters__wrapper">
        <CompanyNameFilter
          value={ensureArray(filters.domains)}
          onChange={(value) => updateFilter("domains", value)}
        />

        <SimilarCompaniesFilter
          value={filters.similar_to_domain}
          onChange={(value) => updateFilter("similar_to_domain", value)}
          isLocked={!entitlement.allowSimilarCompaniesFilter}
        />

        <LocationFilter
          includedContinentValues={ensureArray(filters.location_continent_included)}
          excludedContinentValues={ensureArray(filters.location_continent_excluded)}
          includedBusinessRegionValues={ensureArray(filters.location_business_region_included)}
          excludedBusinessRegionValues={ensureArray(filters.location_business_region_excluded)}
          includedCountryValues={ensureArray(filters.location_country_included)}
          excludedCountryValues={ensureArray(filters.location_country_excluded)}
          includedStateValues={ensureArray(filters.location_state_included)}
          excludedStateValues={ensureArray(filters.location_state_excluded)}
          includedCityValues={ensureArray(filters.location_city_included)}
          excludedCityValues={ensureArray(filters.location_city_excluded)}
          onChangeIncludedContinent={(value) =>
            updateFilter("location_continent_included", value)
          }
          onChangeExcludedContinent={(value) =>
            updateFilter("location_continent_excluded", value)
          }
          onChangeIncludedBusinessRegion={(value) =>
            updateFilter("location_business_region_included", value)
          }
          onChangeExcludedBusinessRegion={(value) =>
            updateFilter("location_business_region_excluded", value)
          }
          onChangeIncludedCountry={(value) => updateFilter("location_country_included", value)}
          onChangeExcludedCountry={(value) => updateFilter("location_country_excluded", value)}
          onChangeIncludedState={(value) => updateFilter("location_state_included", value)}
          onChangeExcludedState={(value) => updateFilter("location_state_excluded", value)}
          onChangeIncludedCity={(value) => updateFilter("location_city_included", value)}
          onChangeExcludedCity={(value) => updateFilter("location_city_excluded", value)}
        />

        <IndustryFilter
          includedValues={ensureArray(filters.industry_included)}
          excludedValues={ensureArray(filters.industry_excluded)}
          industries={filterOptions.industries}
          onChangeIncluded={(value) => updateFilter("industry_included", value)}
          onChangeExcluded={(value) => updateFilter("industry_excluded", value)}
        />

        <CompanySizeFilter
          value={ensureArray(filters.company_size)}
          sizes={filterOptions.company_sizes}
          onChange={(value) => updateFilter("company_size", value)}
        />

        <CompanyTypeFilter
          includedValues={ensureArray(filters.company_type_included)}
          excludedValues={ensureArray(filters.company_type_excluded)}
          companyTypes={filterOptions.company_types}
          onChangeIncluded={(value) => updateFilter("company_type_included", value)}
          onChangeExcluded={(value) => updateFilter("company_type_excluded", value)}
        />

        <FoundedYearFilter
          fromValue={filters.founded_year_from}
          toValue={filters.founded_year_to}
          includedValues={ensureArray(filters.founded_year_included)}
          excludedValues={ensureArray(filters.founded_year_excluded)}
          onChangeFrom={(value) => updateFilter("founded_year_from", value)}
          onChangeTo={(value) => updateFilter("founded_year_to", value)}
          onChangeIncluded={(value) => updateFilter("founded_year_included", value)}
          onChangeExcluded={(value) => updateFilter("founded_year_excluded", value)}
          isLocked={!entitlement.allowFoundedYearFilter}
        />

        <KeywordsFilter
          includedValues={ensureArray(filters.keywords_included)}
          excludedValues={ensureArray(filters.keywords_excluded)}
          operator={(filters.keywords_operator as "and" | "or") || "and"}
          onChangeIncluded={(value) => updateFilter("keywords_included", value)}
          onChangeExcluded={(value) => updateFilter("keywords_excluded", value)}
          onChangeOperator={(value) => updateFilter("keywords_operator", value)}
        />

        <TechnologiesFilter
          includedValues={ensureArray(filters.technology_included)}
          excludedValues={ensureArray(filters.technology_excluded)}
          operator={(filters.technology_operator as "and" | "or") || "and"}
          technologies={filterOptions.technologies || {}}
          onChangeIncluded={(value) => updateFilter("technology_included", value)}
          onChangeExcluded={(value) => updateFilter("technology_excluded", value)}
          onChangeOperator={(value) => updateFilter("technology_operator", value)}
          isLocked={!entitlement.allowTechnologiesFilter}
        />

        <FundingsFilter
          seriesValues={ensureArray(filters.funding_series)}
          amountFrom={filters.funding_amount_from}
          amountTo={filters.funding_amount_to}
          dateValue={filters.funding_date}
          dateFrom={filters.funding_date_from}
          dateTo={filters.funding_date_to}
          onChangeSeriesValues={(value) => updateFilter("funding_series", value)}
          onChangeAmountFrom={(value) => updateFilter("funding_amount_from", value)}
          onChangeAmountTo={(value) => updateFilter("funding_amount_to", value)}
          onChangeDateValue={(value) => updateFilter("funding_date", value)}
          onChangeDateFrom={(value) => updateFilter("funding_date_from", value)}
          onChangeDateTo={(value) => updateFilter("funding_date_to", value)}
          isLocked={!entitlement.allowFundingsFilter}
        />
      </div>
    </>
  )
}
